import {
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';
import { MAX_CUSTOM_ACTORS } from '@/constants/common';
import Modal from '@/components/atoms/Modal';
import Spinner from '@/components/Spinner';
import CreateActorModal from '@/components/videos/CreateActorModal';

import axiosInstance from '@/utils/httpClient';

import { Actor, CustomActor } from '@/types/actor';

interface ActorSelectorModalProps {
  actors: Actor[];
  selectedActor: Actor | null;
  onSelect: (actor: Actor) => void;
  onClose: () => void;
  voices: Voice[];
}

type TabType = 'all' | 'custom';

export const ActorSelectorModal: FC<ActorSelectorModalProps> = ({
  actors,
  selectedActor,
  onSelect,
  onClose,
  voices,
}) => {
  const { t } = useLocale('ai-talking-videos');

  // Custom actors state
  const [customActors, setCustomActors] = useState<CustomActor[]>([]);
  const [isLoadingCustom, setIsLoadingCustom] = useState(true);
  const [canCreate, setCanCreate] = useState(false);
  const [maxActors, setMaxActors] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingActorId, setDeletingActorId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch custom actors on mount
  useEffect(() => {
    fetchCustomActors();
  }, []);

  const fetchCustomActors = async () => {
    setIsLoadingCustom(true);
    try {
      const response = await axiosInstance.get('/api/custom-actors');
      setCustomActors(response.data.actors || []);
      setCanCreate(response.data.canCreate);
      setMaxActors(response.data.maxActors);
      setCurrentCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch custom actors:', error);
    } finally {
      setIsLoadingCustom(false);
    }
  };

  const handleDeleteActor = async (actor: Actor, e: React.MouseEvent) => {
    e.stopPropagation();

    if (
      !window.confirm(
        t('Are you sure you want to delete this actor? This cannot be undone.'),
      )
    ) {
      return;
    }

    setDeletingActorId(actor.id);

    try {
      await axiosInstance.delete('/api/custom-actors', {
        data: { id: actor.id },
      });
      setCustomActors((prev) => prev.filter((a) => a.id !== actor.id));
      setCurrentCount((prev) => prev - 1);
      setCanCreate(currentCount - 1 < maxActors);
      toast.success(t('Actor deleted successfully'));
    } catch (error) {
      toast.error(t('Failed to delete actor'));
    } finally {
      setDeletingActorId(null);
    }
  };

  const handleActorCreated = (actor: CustomActor) => {
    setCustomActors((prev) => [actor, ...prev]);
    setCurrentCount((prev) => prev + 1);
    setCanCreate(currentCount + 1 < maxActors);
    setShowCreateModal(false);
    // Select the newly created actor
    onSelect(actor);
  };

  // Filter actors based on search
  const filterActors = (actorList: Actor[]) => {
    if (!searchQuery.trim()) return actorList;
    const query = searchQuery.toLowerCase();
    return actorList.filter(
      (actor) =>
        actor.name.toLowerCase().includes(query) ||
        actor.description?.toLowerCase().includes(query) ||
        actor.category?.toLowerCase().includes(query),
    );
  };

  const filteredActors = filterActors(actors);
  const filteredCustomActors = filterActors(customActors);

  // Determine what to display based on tab
  const displayActors =
    activeTab === 'custom' ? filteredCustomActors : filteredActors;
  const showCustomSection =
    activeTab === 'all' && filteredCustomActors.length > 0;

  return (
    <>
      <div className='flex flex-col max-h-[85vh]'>
        {/* Header */}
        <div className='px-6 pt-6 pb-4'>
          <div className='text-center mb-4'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-slate-200'>
              {t('Select AI Actor')}
            </h2>
            <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
              {t('Choose an actor or create your own')}
            </p>
          </div>

          {/* Search and Create Button */}
          <div className='flex gap-3 mb-4'>
            <div className='relative flex-1'>
              <input
                type='text'
                placeholder={t('Search actors...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
              />
              <UserCircleIcon className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className={clsxm(
                'flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                'bg-gradient-to-r from-indigo-600 to-purple-600 text-white',
                'hover:from-indigo-700 hover:to-purple-700',
                'shadow-md hover:shadow-lg',
              )}
            >
              <PlusIcon className='h-5 w-5' />
              <span className='hidden sm:inline'>{t('Create Actor')}</span>
            </button>
          </div>

          {/* Tabs */}
          <div className='flex gap-2 border-b border-gray-200 dark:border-gray-700'>
            <button
              onClick={() => setActiveTab('all')}
              className={clsxm(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === 'all'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400',
              )}
            >
              {t('All Actors')}
              <span className='ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700'>
                {actors.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={clsxm(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5',
                activeTab === 'custom'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400',
              )}
            >
              <SparklesIcon className='h-4 w-4' />
              {t('My Actors')}
              <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700'>
                {customActors.length}
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 pb-4'>
          {/* Custom Actors Section (shown in "All" tab) */}
          {showCustomSection && (
            <div className='mb-6'>
              <div className='mb-3 flex items-center gap-2'>
                <SparklesIcon className='h-5 w-5 text-indigo-500' />
                <h3 className='text-sm font-semibold text-gray-900 dark:text-white'>
                  {t('My Actors')}
                </h3>
                <span className='text-xs text-gray-500'>
                  ({currentCount}/{maxActors})
                </span>
              </div>
              <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
                {filteredCustomActors.map((actor) => (
                  <ActorCard
                    key={actor.id}
                    actor={actor}
                    isSelected={selectedActor?.id === actor.id}
                    isCustom={true}
                    isDeleting={deletingActorId === actor.id}
                    onSelect={onSelect}
                    onDelete={(e) => handleDeleteActor(actor, e)}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Loading state for custom tab */}
          {activeTab === 'custom' && isLoadingCustom && (
            <div className='flex items-center justify-center py-12'>
              <Spinner className='h-8 w-8 animate-spin text-indigo-500' />
            </div>
          )}

          {/* Empty state for custom tab */}
          {activeTab === 'custom' &&
            !isLoadingCustom &&
            customActors.length === 0 && (
              <div className='py-12 text-center'>
                <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30'>
                  <SparklesIcon className='h-8 w-8 text-indigo-600 dark:text-indigo-400' />
                </div>
                <h3 className='mb-2 text-lg font-semibold text-gray-900 dark:text-white'>
                  {t('No custom actors yet')}
                </h3>
                <p className='mb-6 text-sm text-gray-500 dark:text-gray-400'>
                  {t('Create your first custom actor to get started')}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className='inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors'
                >
                  <PlusIcon className='h-5 w-5' />
                  {t('Create Actor')}
                </button>
              </div>
            )}

          {/* Pre-built actors header (only in "All" tab when custom actors exist) */}
          {showCustomSection && (
            <div className='mb-3 flex items-center gap-2'>
              <UserCircleIcon className='h-5 w-5 text-gray-500' />
              <h3 className='text-sm font-semibold text-gray-900 dark:text-white'>
                {t('Pre-built Actors')}
              </h3>
            </div>
          )}

          {/* Main actors grid */}
          {activeTab !== 'custom' && (
            <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
              {filteredActors.map((actor) => (
                <ActorCard
                  key={actor.id}
                  actor={actor}
                  isSelected={selectedActor?.id === actor.id}
                  isCustom={false}
                  isDeleting={false}
                  onSelect={onSelect}
                  t={t}
                />
              ))}
            </div>
          )}

          {/* Custom actors grid (for custom tab) */}
          {activeTab === 'custom' &&
            !isLoadingCustom &&
            filteredCustomActors.length > 0 && (
              <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
                {filteredCustomActors.map((actor) => (
                  <ActorCard
                    key={actor.id}
                    actor={actor}
                    isSelected={selectedActor?.id === actor.id}
                    isCustom={true}
                    isDeleting={deletingActorId === actor.id}
                    onSelect={onSelect}
                    onDelete={(e) => handleDeleteActor(actor, e)}
                    t={t}
                  />
                ))}
              </div>
            )}

          {/* No results */}
          {searchQuery &&
            displayActors.length === 0 &&
            (activeTab !== 'custom' || !isLoadingCustom) && (
              <div className='py-12 text-center'>
                <p className='text-gray-500 dark:text-gray-400'>
                  {t('No actors found matching "{{query}}"', {
                    query: searchQuery,
                  })}
                </p>
              </div>
            )}
        </div>

        {/* Footer */}
        <div className='border-t border-gray-200 px-6 py-4 dark:border-gray-700'>
          <div className='flex justify-between items-center'>
            <p className='text-xs text-gray-500 dark:text-gray-400'>
              {t('Custom actors')}: {currentCount}/{maxActors}
            </p>
            <button
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600
                rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>

      {/* Create Actor Modal */}
      <Modal
        showModal={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        className='sm:max-w-2xl overflow-visible'
        doNotCloseOnOverlayClick={true}
        hideCloseButton={true}
      >
        <CreateActorModal
          voices={voices}
          onClose={() => setShowCreateModal(false)}
          onActorCreated={handleActorCreated}
          canCreate={canCreate}
          maxActors={maxActors}
          currentCount={currentCount}
        />
      </Modal>
    </>
  );
};

// Actor card component
interface ActorCardProps {
  actor: Actor;
  isSelected: boolean;
  isCustom: boolean;
  isDeleting: boolean;
  onSelect: (actor: Actor) => void;
  onDelete?: (e: React.MouseEvent) => void;
  t: (key: string, options?: any) => string;
}

const ActorCard: FC<ActorCardProps> = ({
  actor,
  isSelected,
  isCustom,
  isDeleting,
  onSelect,
  onDelete,
  t,
}) => {
  return (
    <button
      onClick={() => onSelect(actor)}
      disabled={isDeleting}
      className={clsxm(
        'group relative rounded-xl border-2 transition-all duration-200 text-left overflow-hidden',
        'hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
        isSelected
          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 shadow-md ring-2 ring-indigo-500/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-600',
        isDeleting && 'opacity-50 pointer-events-none',
      )}
    >
      {/* Actor Photo */}
      <div className='aspect-[3/4] w-full relative'>
        <Image
          src={actor.photo}
          alt={actor.name}
          fill
          sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
          className='object-cover'
        />

        {/* Custom badge */}
        {isCustom && (
          <div className='absolute top-2 left-2'>
            <span className='inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2 py-0.5 text-xs font-medium text-white shadow-sm'>
              <SparklesIcon className='h-3 w-3' />
              {t('Custom')}
            </span>
          </div>
        )}

        {/* Delete button for custom actors */}
        {isCustom && onDelete && (
          <button
            onClick={onDelete}
            className='absolute top-2 left-2 rounded-full bg-red-500/90 p-1.5 text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 hover:bg-red-600'
          >
            {isDeleting ? (
              <Spinner className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <TrashIcon className='h-3.5 w-3.5' />
            )}
          </button>
        )}

        {/* Selection indicator */}
        {isSelected && (
          <div className='absolute top-2 right-2'>
            <div className='flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 shadow-md'>
              <CheckIcon className='h-4 w-4 text-white' />
            </div>
          </div>
        )}
      </div>

      {/* Actor Info */}
      <div className='p-3'>
        <h4 className='font-semibold text-gray-900 dark:text-gray-100 text-sm truncate'>
          {actor.name}
        </h4>
        {actor.description && (
          <p className='mt-0.5 text-xs text-gray-600 dark:text-gray-400 line-clamp-2'>
            {actor.description}
          </p>
        )}
      </div>
    </button>
  );
};

export default ActorSelectorModal;
